/**
 * UserMenu: dropdown anclado al avatar del Header.
 *
 * Sprint 14 S14-13 (Bloque D extendido). Reemplaza al
 * `RolSelectorCombobox` (que estaba `hidden md:inline` y dejaba a los
 * usuarios mobile sin manera de cambiar a Comisión/Tesorería) y absorbe
 * los 3 links que vivían en el Footer (Ayuda / Datos para transferir /
 * Cerrar sesión).
 *
 * Estructura del dropdown:
 * 1. Header del menu: nombre completo + email.
 * 2. Sección cambio de vista (solo si la persona tiene >=1 rol operativo
 *    además de vecino). Radios marcan la vista activa.
 * 3. Sección acciones: Mis datos / Datos para transferir / Ayuda.
 * 4. Cerrar sesión.
 *
 * El dropdown sigue el patrón Bell para el aria/click-outside/ESC.
 * En mobile: ancho fijo 260px, anclado abajo-derecha del avatar.
 * En desktop: idéntico.
 */
import { useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../lib/auth/AuthContext';
import { Avatar } from '../ui/Avatar';
import { DatosCuentaModal } from '../ui/DatosCuentaModal';
import { useMenuAnclado } from '../ui/useMenuAnclado';
import type { AuthMeResponse } from '../../lib/api/types';
import { vistaActualPorPath, type VistaActiva } from '../../lib/layout/vista';

interface UserMenuProps {
  me: AuthMeResponse;
  /** Override del pathname para previews dev-only (S14-10 ShellPreview).
   *  Default: usa `useLocation().pathname`. */
  pathnameOverride?: string;
  /** Si está, se invoca en lugar de navegar con react-router al cambiar
   *  vista. Permite que el preview sincronice su pathname simulado en
   *  vez de salir del preview. Sprint 14 S14-13. */
  onCambiarVista?: (vista: VistaActiva) => void;
}

function primerNombre(nombreCompleto: string): string {
  const trimmed = (nombreCompleto || '').trim();
  if (!trimmed) return '';
  const primera = trimmed.split(/\s+/)[0]!;
  return primera[0]!.toUpperCase() + primera.slice(1).toLowerCase();
}

const LABEL_VISTA: Record<VistaActiva, string> = {
  vecino: 'Vecino',
  comision: 'Comisión',
  tesoreria: 'Tesorería',
};

const RUTA_VISTA: Record<VistaActiva, string> = {
  vecino: '/vecino',
  comision: '/admin/solicitudes-acceso',
  tesoreria: '/tesoreria',
};

function IconChevron() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="opacity-80"
    >
      <path d="M6 9 L12 15 L18 9" />
    </svg>
  );
}

interface MenuItemProps {
  onClick: () => void;
  label: string;
  /** Si está definido, renderiza marcador de radio: ● activo, ○ inactivo. */
  activo?: boolean;
  /** Color naranja (para "Datos para transferir"). */
  accent?: boolean;
  /** Separador visual delante (gris muy sutil). */
  destacado?: boolean;
}

function MenuItem({ onClick, label, activo, accent, destacado }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-bg-elevated transition-colors duration-base focus:outline-none focus:bg-bg-elevated ${
        accent
          ? 'text-orange font-medium'
          : destacado
            ? 'text-text-secondary'
            : 'text-text-primary'
      }`}
    >
      {activo !== undefined && (
        <span
          aria-hidden="true"
          className={`w-4 inline-flex justify-center text-xs ${
            activo ? 'text-orange' : 'text-text-muted'
          }`}
        >
          {activo ? '●' : '○'}
        </span>
      )}
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}

export function UserMenu({ me, pathnameOverride, onCambiarVista }: UserMenuProps) {
  // useContext directo (en vez de useAuth) para que el componente se
  // pueda montar en previews/tests sin AuthProvider activo. Cuando no
  // hay context, signOut es un no-op.
  const authCtx = useContext(AuthContext);
  const signOut = authCtx?.signOut ?? (async () => {});
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = pathnameOverride ?? location.pathname;

  // Dropdown anclado al avatar, renderizado en portal (escapa del
  // overflow-hidden del shell). Cierra con click-fuera/ESC/scroll/resize.
  const { abierto, coords, botonRef, menuRef, alternar, cerrar } =
    useMenuAnclado(pathname);
  // Sprint 15 S15-01: modal "Datos para transferir" se abre desde el
  // item del menu. Mantener el state aquí en lugar del componente padre
  // para no propagar prop callback por cada página del shell.
  const [modalDatosAbierto, setModalDatosAbierto] = useState(false);

  const tieneComision = me.roles_vigentes.includes('comision');
  const tieneTesorero = me.roles_vigentes.includes('tesorero');
  const tieneCambioVista = tieneComision || tieneTesorero;
  const vistaActual = vistaActualPorPath(pathname);

  const cambiarVista = useCallback(
    (v: VistaActiva) => {
      cerrar();
      if (onCambiarVista) {
        onCambiarVista(v);
        return;
      }
      navigate(RUTA_VISTA[v]);
    },
    [cerrar, navigate, onCambiarVista],
  );

  const irA = useCallback(
    (ruta: string) => {
      cerrar();
      navigate(ruta);
    },
    [cerrar, navigate],
  );

  const handleSignOut = useCallback(async () => {
    cerrar();
    await signOut();
    navigate('/login', { replace: true });
  }, [cerrar, signOut, navigate]);

  return (
    <div className="pl-3">
      <button
        ref={botonRef}
        type="button"
        onClick={alternar}
        aria-expanded={abierto}
        aria-haspopup="menu"
        aria-label="Menú de usuario"
        className="flex items-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40 rounded-sm p-1"
      >
        <Avatar variant="header" nombreCompleto={me.persona.nombre_completo} />
        <span className="text-base text-white hidden sm:inline">
          {primerNombre(me.persona.nombre_completo)}
        </span>
        <IconChevron />
      </button>

      {abierto &&
        coords &&
        createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Opciones de usuario"
          style={{ position: 'fixed', top: coords.top, right: coords.right }}
          className="w-64 bg-bg-card border-[0.5px] border-white/15 rounded-md shadow-2xl z-50 overflow-hidden"
        >
          {/* Header del menu: nombre + email */}
          <div className="px-4 py-3 border-b-[0.5px] border-white/[0.08]">
            <div className="text-sm font-medium text-text-primary truncate">
              {me.persona.nombre_completo}
            </div>
            <div className="text-xs text-text-secondary truncate">
              {me.persona.email}
            </div>
          </div>

          {/* Cambio de vista (solo si hay rol operativo extra) */}
          {tieneCambioVista && (
            <div className="border-b-[0.5px] border-white/[0.08] py-1">
              <div className="px-4 pt-2 pb-1 text-[10px] font-medium text-text-muted uppercase tracking-wide">
                Vista actual
              </div>
              <MenuItem
                onClick={() => cambiarVista('vecino')}
                activo={vistaActual === 'vecino'}
                label={LABEL_VISTA.vecino}
              />
              {tieneComision && (
                <MenuItem
                  onClick={() => cambiarVista('comision')}
                  activo={vistaActual === 'comision'}
                  label={LABEL_VISTA.comision}
                />
              )}
              {tieneTesorero && (
                <MenuItem
                  onClick={() => cambiarVista('tesoreria')}
                  activo={vistaActual === 'tesoreria'}
                  label={LABEL_VISTA.tesoreria}
                />
              )}
            </div>
          )}

          {/* Acciones del shell (lo que estaba en el Footer) */}
          <div className="border-b-[0.5px] border-white/[0.08] py-1">
            <MenuItem onClick={() => irA('/vecino/mis-datos')} label="Mis datos" />
            <MenuItem
              onClick={() => {
                cerrar();
                setModalDatosAbierto(true);
              }}
              label="Datos para transferir"
              accent
            />
            <MenuItem
              onClick={() =>
                irA(vistaActual === 'comision' ? '/admin/ayuda' : '/ayuda')
              }
              label="Ayuda"
            />
          </div>

          {/* Cerrar sesión */}
          <div className="py-1">
            <MenuItem onClick={handleSignOut} label="Cerrar sesión" />
          </div>
        </div>,
          document.body,
        )}

      {/* Modal "Datos para transferir" — Sprint 15 S15-01. */}
      {modalDatosAbierto && (
        <DatosCuentaModal onClose={() => setModalDatosAbierto(false)} />
      )}
    </div>
  );
}
